/**
 * ReportViewModel → DocSpec.
 *
 * Pure and synchronous: no DOM, no jsPDF, no React. That is what makes the
 * exported content unit-testable and what stops Summary/Detailed from drifting
 * apart — they are the same traversal with a different section selection.
 */
import { signalsFromInsight, } from '../report-adapter';
// ── small helpers ────────────────────────────────────────────────────────────
const tone = (t) => (t !== null && t !== void 0 ? t : 'neutral');
const nz = (v) => {
    const s = (v !== null && v !== void 0 ? v : '').trim();
    return s.length ? s : null;
};
/** "12/20 (60%)" / "12" / "N/A" — the score shorthand used across the report. */
const scoreOutOf = (score, outOf, pct) => {
    if (score == null)
        return null;
    const base = outOf != null ? `${score}/${outOf}` : `${score}`;
    return pct != null ? `${base} (${pct}%)` : base;
};
const pctLabel = (v) => (v != null ? `${v}%` : 'N/A');
/** Seconds since capture start → "mm:ss". */
export const clockLabel = (sec) => {
    if (sec == null || !Number.isFinite(sec) || sec < 0)
        return '—';
    const whole = Math.floor(sec);
    const m = Math.floor(whole / 60);
    const s = whole % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};
const severityTone = (severity) => severity === 'high' ? 'danger' : severity === 'medium' ? 'warning' : 'neutral';
const trailOf = (parts) => {
    const joined = parts.filter(Boolean).join(' › ');
    return joined.length ? joined : null;
};
/** Signals line for a transcript turn, e.g. "Attention: 82%  ·  Risk: low". */
const insightLine = (insight) => {
    if (!insight)
        return null;
    const signals = signalsFromInsight(insight)
        .map((s) => `${s.label}: ${s.value}`)
        .join('  ·  ');
    const risk = insight.risk && insight.risk !== 'low' ? `[${insight.risk} risk] ` : '';
    const brief = nz(insight.brief);
    const tail = brief ? `${risk}${brief}` : risk.trim();
    return [signals, tail].filter(Boolean).join('\n') || null;
};
// ── section builders ─────────────────────────────────────────────────────────
/**
 * Executive summary. Shared by both variants verbatim, so "Export Summary
 * Report" always reads exactly like the on-screen Executive Summary tab.
 */
const executiveSummary = (vm) => {
    var _a, _b, _c, _d, _e;
    const sections = [];
    sections.push({ kind: 'heading', level: 1, text: 'Executive Summary' });
    if (nz(vm.summary.reason)) {
        sections.push({ kind: 'paragraph', text: vm.summary.reason });
    }
    sections.push({
        kind: 'stats',
        stats: [
            { label: 'Objectives', value: `${(_a = vm.counts.objectives) !== null && _a !== void 0 ? _a : '—'}` },
            { label: 'Criteria', value: `${(_b = vm.counts.criteria) !== null && _b !== void 0 ? _b : '—'}` },
            { label: 'Evidence', value: `${(_c = vm.counts.evidence) !== null && _c !== void 0 ? _c : '—'}` },
            { label: 'Questions', value: `${(_d = vm.counts.questionsAsked) !== null && _d !== void 0 ? _d : '—'}` },
            { label: 'Coding', value: `${(_e = vm.counts.codingChallenges) !== null && _e !== void 0 ? _e : '—'}` },
        ],
    });
    if (vm.summary.strengths.length) {
        sections.push({ kind: 'bullets', title: 'Key Strengths', items: vm.summary.strengths });
    }
    if (vm.summary.improvements.length) {
        sections.push({
            kind: 'bullets',
            title: 'Areas for Improvement',
            items: vm.summary.improvements,
        });
    }
    if (vm.objectives.length) {
        sections.push({ kind: 'heading', level: 2, text: 'Objective Progress' });
        vm.objectives.forEach((obj) => {
            sections.push({
                kind: 'meter',
                label: obj.title,
                valueLabel: pctLabel(obj.score),
                pct: obj.score,
                tone: tone(obj.band.tone),
            });
        });
    }
    // Evidence spread + proctoring one-liner, mirroring the two side-by-side
    // cards on the Executive Summary tab.
    const bandCounts = { success: 0, warning: 0, danger: 0, neutral: 0 };
    vm.evidence.forEach((e) => {
        bandCounts[e.band.tone] = (bandCounts[e.band.tone] || 0) + 1;
    });
    const overviewRows = [
        {
            label: 'Evidence overview',
            value: `${vm.evidence.length} item(s) — ${bandCounts.success} strong · ${bandCounts.warning} moderate · ${bandCounts.danger} needs work`,
        },
    ];
    if (vm.proctoring.available) {
        const headline = vm.proctoring.events.length ? 'Signals noted' : 'No flags';
        const cards = vm.proctoring.cards.map((c) => `${c.label}: ${c.value}`).join('  ·  ');
        overviewRows.push({
            label: 'Proctoring snapshot',
            value: [headline, cards].filter(Boolean).join(' — '),
        });
    }
    else {
        overviewRows.push({ label: 'Proctoring snapshot', value: 'Not available' });
    }
    sections.push({ kind: 'keyValue', rows: overviewRows });
    if (vm.timeline.length) {
        sections.push({
            kind: 'bullets',
            title: 'Interview Timeline',
            items: vm.timeline.map((item) => `[${item.label}] ${item.text}`),
        });
    }
    return sections;
};
const evaluationCriteria = (vm) => {
    if (!vm.criteria.length) {
        return [
            { kind: 'heading', level: 1, text: 'Evaluation Criteria' },
            { kind: 'paragraph', text: 'No flat criteria list available.', muted: true },
        ];
    }
    const rows = vm.criteria.map((c) => {
        var _a, _b;
        return [
            c.title,
            {
                text: (_a = scoreOutOf(c.score, c.target)) !== null && _a !== void 0 ? _a : 'N/A',
                align: 'right',
                bold: true,
                tone: tone(c.band.tone),
            },
            { text: pctLabel(c.pct), align: 'right', tone: tone(c.band.tone) },
            { text: c.met == null ? '—' : c.met ? 'Yes' : 'No', align: 'center' },
            (_b = nz(c.reason)) !== null && _b !== void 0 ? _b : '—',
        ];
    });
    return [
        { kind: 'heading', level: 1, text: 'Evaluation Criteria' },
        {
            kind: 'table',
            // Weights are tuned so every header fits on one line at the table font
            // size — a wrapped "Achieve/d" reads like a rendering bug.
            columns: [
                { header: 'Criterion', weight: 3 },
                { header: 'Score', weight: 1.2, align: 'right' },
                { header: 'Achieved', weight: 1.5, align: 'right' },
                { header: 'Met', weight: 0.9, align: 'center' },
                { header: 'Rationale', weight: 4.4 },
            ],
            rows,
        },
    ];
};
const objectives = (vm) => {
    const sections = [{ kind: 'heading', level: 1, text: 'Objectives' }];
    if (!vm.objectives.length) {
        sections.push({
            kind: 'paragraph',
            text: 'No evaluation framework data available.',
            muted: true,
        });
        return sections;
    }
    vm.objectives.forEach((obj) => {
        sections.push({
            kind: 'heading',
            level: 2,
            text: `${obj.index}. ${obj.title} — ${pctLabel(obj.score)}`,
        });
        if (!obj.subObjectives.length) {
            sections.push({ kind: 'paragraph', text: 'No sub-objectives recorded.', muted: true });
            return;
        }
        obj.subObjectives.forEach((sub) => {
            sections.push({
                kind: 'meter',
                label: sub.title,
                valueLabel: pctLabel(sub.score),
                pct: sub.score,
                tone: tone(sub.band.tone),
            });
            if (sub.criteria.length) {
                sections.push({
                    kind: 'table',
                    columns: [
                        { header: 'Criterion', weight: 3 },
                        { header: 'Score', weight: 1, align: 'right' },
                        { header: 'Rationale', weight: 6 },
                    ],
                    rows: sub.criteria.map((c) => {
                        var _a, _b;
                        return [
                            c.title,
                            {
                                text: (_a = scoreOutOf(c.score, c.target)) !== null && _a !== void 0 ? _a : 'N/A',
                                align: 'right',
                                bold: true,
                                tone: tone(c.band.tone),
                            },
                            (_b = nz(c.reason)) !== null && _b !== void 0 ? _b : '—',
                        ];
                    }),
                });
            }
            // Questions carry free text and sometimes code — qa blocks, not rows.
            [...sub.questions, ...sub.codingQuestions].forEach((q) => {
                var _a;
                sections.push({
                    kind: 'qa',
                    question: (_a = nz(q.question)) !== null && _a !== void 0 ? _a : 'Question',
                    answer: q.code ? null : nz(q.answer),
                    code: nz(q.code),
                    language: q.language,
                    evaluation: nz(q.reason),
                    scoreLabel: scoreOutOf(q.score, q.outOf, q.pct),
                    tone: 'neutral',
                });
            });
        });
    });
    return sections;
};
const evidence = (vm) => {
    const sections = [{ kind: 'heading', level: 1, text: 'Evidence' }];
    if (!vm.evidence.length) {
        sections.push({
            kind: 'paragraph',
            text: 'No structured evidence was captured for this interview.',
            muted: true,
        });
        return sections;
    }
    vm.evidence.forEach((e) => {
        var _a;
        sections.push({
            kind: 'qa',
            question: (_a = nz(e.question)) !== null && _a !== void 0 ? _a : (e.isCoding ? 'Coding response' : 'Candidate response'),
            trail: trailOf([e.objectiveTitle, e.subObjectiveTitle, e.criterionTitle]),
            answer: e.code ? null : nz(e.response),
            code: nz(e.code),
            language: e.language,
            evaluationLabel: 'AI Evaluation',
            evaluation: nz(e.evaluation),
            scoreLabel: scoreOutOf(e.score, e.outOf),
            tone: tone(e.band.tone),
        });
    });
    return sections;
};
const coding = (vm) => {
    if (!vm.coding.length)
        return [];
    const sections = [{ kind: 'heading', level: 1, text: 'Coding Challenges' }];
    vm.coding.forEach((c) => {
        var _a, _b, _c, _d;
        const facts = [];
        if (c.syntaxValid != null)
            facts.push(`Syntax valid: ${c.syntaxValid ? 'Yes' : 'No'}`);
        if (c.totalTestCases != null) {
            facts.push(`Test cases: ${(_a = c.testCasesPassed) !== null && _a !== void 0 ? _a : 0}/${c.totalTestCases} passed`);
        }
        if (nz(c.language))
            facts.push(`Language: ${c.language}`);
        sections.push({
            kind: 'qa',
            question: (_c = (_b = nz(c.title)) !== null && _b !== void 0 ? _b : nz(c.question)) !== null && _c !== void 0 ? _c : 'Coding challenge',
            trail: trailOf([c.objectiveTitle, c.subObjectiveTitle]),
            // When the card has both a prompt and a title, keep the prompt as prose.
            answer: nz(c.title) && nz(c.question) ? c.question : null,
            code: nz(c.code),
            language: c.language,
            evaluationLabel: 'AI Feedback',
            evaluation: (_d = nz(c.aiFeedback)) !== null && _d !== void 0 ? _d : nz(c.evaluation),
            scoreLabel: scoreOutOf(c.score, c.outOf, c.pct),
            tone: tone(c.band.tone),
            footnote: facts.length ? facts.join('  ·  ') : null,
        });
    });
    return sections;
};
const behaviour = (vm) => {
    if (!vm.behaviour.length)
        return [];
    return [
        { kind: 'heading', level: 1, text: 'Behaviour Evaluation' },
        {
            kind: 'table',
            columns: [
                { header: 'Attribute', weight: 3 },
                { header: 'Score', weight: 1.2, align: 'right' },
                { header: 'Achieved', weight: 1.5, align: 'right' },
                { header: 'Rationale', weight: 5.3 },
            ],
            rows: vm.behaviour.map((c) => {
                var _a, _b;
                return [
                    c.title,
                    {
                        text: (_a = scoreOutOf(c.score, c.target)) !== null && _a !== void 0 ? _a : 'N/A',
                        align: 'right',
                        bold: true,
                        tone: tone(c.band.tone),
                    },
                    { text: pctLabel(c.pct), align: 'right', tone: tone(c.band.tone) },
                    (_b = nz(c.reason)) !== null && _b !== void 0 ? _b : '—',
                ];
            }),
        },
    ];
};
const proctoring = (vm) => {
    var _a;
    const sections = [{ kind: 'heading', level: 1, text: 'Proctoring & Integrity' }];
    const p = vm.proctoring;
    if (!p.available) {
        sections.push({
            kind: 'paragraph',
            text: 'Proctoring data was not available for this interview.',
            muted: true,
        });
        return sections;
    }
    sections.push({
        kind: 'stats',
        stats: [
            { label: 'Integrity', value: p.integrityScore != null ? `${p.integrityScore}` : 'N/A' },
            { label: 'Events', value: `${p.charts.totalEvents}` },
            { label: 'Review Needed', value: p.reviewRecommended ? 'Yes' : 'No' },
            {
                label: 'Face Present',
                value: p.charts.facePresencePct != null ? `${p.charts.facePresencePct}%` : 'N/A',
            },
            {
                label: 'Single Person',
                value: p.charts.singlePersonPct != null ? `${p.charts.singlePersonPct}%` : 'N/A',
            },
            { label: 'Tab Switches', value: p.tabSwitches != null ? `${p.tabSwitches}` : 'N/A' },
        ],
    });
    const verdict = [
        nz(p.verdictLabel),
        p.integrityScore != null
            ? `Integrity score ${p.integrityScore}/100${p.integrityBand ? ` (${p.integrityBand})` : ''}`
            : null,
    ]
        .filter(Boolean)
        .join(' — ');
    if (verdict) {
        sections.push({ kind: 'paragraph', text: verdict, bold: true, tone: tone(p.verdictTone) });
    }
    const notes = [...p.verdictReasons, ...p.summarySentences].filter((s) => nz(s));
    if (notes.length)
        sections.push({ kind: 'bullets', items: notes });
    if (p.events.length) {
        sections.push({
            kind: 'table',
            title: 'Flagged Events',
            columns: [
                { header: 'Start', weight: 1, align: 'right' },
                { header: 'Event', weight: 4 },
                { header: 'Severity', weight: 1.4, align: 'center' },
                { header: 'Duration', weight: 1.4, align: 'right' },
                { header: 'Peak', weight: 1.4, align: 'right' },
            ],
            rows: p.events.map((ev) => [
                { text: clockLabel(ev.start), align: 'right' },
                ev.label,
                {
                    text: ev.severity,
                    align: 'center',
                    bold: true,
                    tone: severityTone(ev.severity),
                },
                { text: `${ev.durationSec.toFixed(0)}s`, align: 'right' },
                {
                    text: Number.isFinite(ev.peakMagnitude)
                        ? `${ev.peakMagnitude.toFixed(1)}${ev.unit || ''}`
                        : '—',
                    align: 'right',
                },
            ]),
        });
    }
    // The on-screen tab draws these as recharts SVG. SVG can't be lifted into a
    // PDF without a rasterise step, and the underlying series are already plain
    // numbers — so the export states them as tables instead of pictures.
    if (p.charts.eventsByType.length) {
        sections.push({
            kind: 'table',
            title: 'Signal Distribution',
            columns: [
                { header: 'Signal', weight: 4 },
                { header: 'Occurrences', weight: 1.6, align: 'right' },
            ],
            rows: p.charts.eventsByType.map((e) => [
                e.label,
                { text: `${e.count}`, align: 'right' },
            ]),
        });
    }
    if (p.charts.gazeDistribution.length) {
        sections.push({
            kind: 'table',
            title: 'Gaze Distribution',
            columns: [
                { header: 'Direction', weight: 4 },
                { header: 'Share', weight: 1.6, align: 'right' },
            ],
            rows: p.charts.gazeDistribution.map((g) => [
                g.label,
                { text: `${g.pct}%`, align: 'right' },
            ]),
        });
    }
    if ((_a = p.bodyLanguage) === null || _a === void 0 ? void 0 : _a.available) {
        sections.push({
            kind: 'keyValue',
            title: 'Body Language',
            rows: [
                { label: 'Posture', value: p.bodyLanguage.posture },
                { label: 'Openness', value: p.bodyLanguage.openness },
                { label: 'Fidgeting', value: p.bodyLanguage.fidget },
            ],
        });
    }
    if (p.confidence) {
        const rows = [
            { label: 'Band', value: p.confidence.band || 'N/A' },
            { label: 'Trend', value: p.confidence.trend || 'N/A' },
        ];
        if (p.confidence.drivers.length) {
            rows.push({ label: 'Drivers', value: p.confidence.drivers.join('  ·  ') });
        }
        sections.push({ kind: 'keyValue', title: 'Confidence', rows });
        if (nz(p.confidence.disclaimer)) {
            sections.push({ kind: 'paragraph', text: p.confidence.disclaimer, muted: true });
        }
    }
    return sections;
};
const transcript = (entries) => {
    const sections = [{ kind: 'heading', level: 1, text: 'Interview Transcript' }];
    if (!entries.length) {
        sections.push({ kind: 'paragraph', text: 'No transcript was recorded.', muted: true });
        return sections;
    }
    sections.push({
        kind: 'table',
        columns: [
            { header: 'Time', weight: 1, align: 'right' },
            { header: 'Speaker', weight: 1.8 },
            { header: 'Utterance', weight: 8 },
        ],
        rows: entries.map((t) => {
            var _a;
            const signals = insightLine(t.proctoringInsight);
            const body = [(_a = nz(t.text)) !== null && _a !== void 0 ? _a : '—', signals ? `Signals — ${signals}` : null]
                .filter(Boolean)
                .join('\n\n');
            const speaker = t.score != null ? `${t.speakerName}\n${Math.round(t.score)}` : t.speakerName;
            return [
                { text: t.timeLabel || '—', align: 'right' },
                { text: speaker, bold: t.speaker === 'interviewer' },
                body,
            ];
        }),
    });
    return sections;
};
const recommendation = (vm) => {
    const sections = [{ kind: 'heading', level: 1, text: 'Final Recommendation' }];
    const score = vm.score.value != null
        ? `${vm.score.value}/100${vm.score.passingScore != null ? ` (pass at ${vm.score.passingScore})` : ''}`
        : null;
    sections.push({
        kind: 'banner',
        title: vm.score.recommendationLabel,
        scoreLabel: score,
        body: nz(vm.summary.reason),
        tone: tone(vm.score.tone),
    });
    if (vm.summary.strengths.length) {
        sections.push({ kind: 'bullets', title: 'Strengths', items: vm.summary.strengths });
    }
    if (vm.summary.improvements.length) {
        sections.push({
            kind: 'bullets',
            title: 'Areas for Improvement',
            items: vm.summary.improvements,
        });
    }
    return sections;
};
// ── entry point ──────────────────────────────────────────────────────────────
export const buildReportDoc = (vm, variant, options = {}) => {
    var _a, _b, _c;
    const { meta } = vm;
    const candidate = (_a = nz(meta.candidateName)) !== null && _a !== void 0 ? _a : 'Candidate';
    const role = (_b = nz(meta.role)) !== null && _b !== void 0 ? _b : 'Interview Report';
    const coverRows = [
        { label: 'Candidate', value: candidate },
        { label: 'Email', value: (_c = nz(meta.candidateEmail)) !== null && _c !== void 0 ? _c : '—' },
        meta.candidatePhone ? { label: 'Phone', value: meta.candidatePhone } : null,
        { label: 'Role', value: role },
        meta.interviewLevel ? { label: 'Level', value: meta.interviewLevel } : null,
        meta.interviewType ? { label: 'Interview Type', value: meta.interviewType } : null,
        { label: 'Status', value: meta.statusLabel },
        nz(options.dateLabel) ? { label: 'Date', value: options.dateLabel } : null,
        meta.actualDurationLabel ? { label: 'Duration', value: meta.actualDurationLabel } : null,
        meta.scheduledDurationMin != null
            ? { label: 'Scheduled For', value: `${meta.scheduledDurationMin} min` }
            : null,
        meta.takenBy ? { label: 'Taken By', value: meta.takenBy } : null,
    ].filter(Boolean);
    const sections = [];
    if (variant === 'transcript') {
        sections.push(...transcript(vm.transcript));
    }
    else {
        sections.push(...executiveSummary(vm));
        if (variant === 'detailed') {
            sections.push(...evaluationCriteria(vm));
            sections.push(...objectives(vm));
            sections.push(...evidence(vm));
            sections.push(...coding(vm));
            sections.push(...behaviour(vm));
            sections.push(...proctoring(vm));
            sections.push(...recommendation(vm));
            // Transcript last: it is by far the longest section and would otherwise
            // bury the evaluation for anyone skimming the front of the document.
            sections.push(...transcript(vm.transcript));
        }
    }
    const links = [];
    if (nz(meta.recordingUrl)) {
        links.push({
            kind: 'link',
            label: 'AI interview recording',
            href: meta.recordingUrl,
        });
    }
    if (variant === 'summary' && nz(options.reportUrl)) {
        links.push({
            kind: 'link',
            label: 'View full interview questions & answers report',
            href: options.reportUrl,
        });
    }
    if (links.length) {
        sections.push({ kind: 'heading', level: 2, text: 'Links' }, ...links);
    }
    const kicker = variant === 'summary'
        ? 'Interview Summary Report'
        : variant === 'transcript'
            ? 'Interview Transcript'
            : 'Interview Evaluation Report';
    return {
        documentTitle: `${kicker} — ${candidate}`,
        footerLabel: `${candidate} · ${role}`,
        cover: {
            kicker,
            title: role,
            subtitle: candidate,
            scoreLabel: vm.score.value != null ? `${vm.score.value}/100` : null,
            scoreCaption: nz(vm.score.recommendationLabel),
            tone: tone(vm.score.tone),
            rows: coverRows,
        },
        sections,
    };
};
